import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { SetupPackageBuildDialog } from '@/core/SetupPackageBuildDialog';
import { planScenarioService } from '@/services/finance/PlanScenarioService';

interface SaveAsSetupPackageDialogProps {
  /** The plan scenario whose setup closure is packaged. */
  entityId: string;
  open: boolean;
  onClose: () => void;
}

/**
 * "Save setup as package…" (spec: docs/15-designspecs/Setup-Packages.md): runs the Finance
 * closure builder against this scenario — entry views, statement templates and derivation rules
 * travel with it, values never do — and stores the result as a Setup Package that can be applied
 * here or exported to another environment.
 */
export function SaveAsSetupPackageDialog({ entityId, open, onClose }: SaveAsSetupPackageDialogProps) {
  const { t } = useTranslation();

  const { data: scenario } = useQuery({
    queryKey: ['plan-scenario', entityId],
    queryFn: () => planScenarioService.getById(entityId),
    enabled: open,
  });

  return (
    <SetupPackageBuildDialog
      moduleName="Finance"
      rootEntityName="PlanScenario"
      rootId={entityId}
      title={t('Finance.SaveAsSetupPackage.Title', { defaultValue: 'Save setup as package…' })}
      intro={t('Finance.SaveAsSetupPackage.Intro', {
        defaultValue: 'Packages the setup around "{{source}}": entry views, statement templates and account rules. Headers and values are not included.',
        source: scenario?.name ?? '…',
      })}
      open={open}
      onClose={onClose}
    />
  );
}
